/**
 * validationHierarchy.ts
 *
 * Frontend mirror of the backend VALIDATION_HIERARCHY
 * (backend/src/modules/profile-validation/profile-validation.service.ts).
 *
 * Defines which roles are allowed to validate a given user's profile, so the
 * "Valider" button is only shown when the current user actually has the right
 * to validate that specific role. Keep in sync with the backend.
 */
import { UserRole } from '@/types/global.types';

// For each target role: the roles allowed to validate that profile.
const VALIDATION_HIERARCHY: Record<UserRole, UserRole[]> = {
  [UserRole.STUDENT]: [
    UserRole.ACADEMIC_COORDINATOR,
    UserRole.PROGRAM_HEAD,
    UserRole.PEDAGOGICAL_DIRECTOR,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.PARENT]: [],
  [UserRole.TEACHER]: [
    UserRole.PROGRAM_HEAD,
    UserRole.PEDAGOGICAL_DIRECTOR,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.ACADEMIC_COORDINATOR]: [
    UserRole.PROGRAM_HEAD,
    UserRole.PEDAGOGICAL_DIRECTOR,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.PROGRAM_HEAD]: [
    UserRole.PEDAGOGICAL_DIRECTOR,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.FINANCIAL_MANAGER]: [UserRole.BRANCH_MANAGER, UserRole.DIRECTOR_GENERAL],
  [UserRole.MARKETING_MANAGER]: [UserRole.BRANCH_MANAGER, UserRole.DIRECTOR_GENERAL],
  [UserRole.COMMERCIAL_MANAGER]: [
    UserRole.MARKETING_MANAGER,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.CALL_CENTER_AGENT]: [
    UserRole.MARKETING_MANAGER,
    UserRole.BRANCH_MANAGER,
    UserRole.DIRECTOR_GENERAL,
  ],
  [UserRole.PEDAGOGICAL_DIRECTOR]: [UserRole.BRANCH_MANAGER, UserRole.DIRECTOR_GENERAL],
  [UserRole.BRANCH_MANAGER]: [UserRole.DIRECTOR_GENERAL],
  [UserRole.DIRECTOR_GENERAL]: [],
  [UserRole.SUPER_ADMIN]: [],
};

/**
 * Whether a user with `validatorRole` is allowed (by hierarchy) to validate the
 * profile of a user with `targetRole`. Campus scoping is still enforced
 * server-side; this only governs the role-level permission and button display.
 */
export function canValidateRole(
  validatorRole: UserRole | string | undefined,
  targetRole: UserRole | string | undefined,
): boolean {
  if (!validatorRole || !targetRole) return false;
  const validators = VALIDATION_HIERARCHY[targetRole as UserRole] || [];
  return validators.includes(validatorRole as UserRole);
}
